import clsx from 'clsx'
import DropdownSortingMenu from 'components/DropdownSortingMenu'
import GridSpinner from 'components/UI/GridSpinner'
import { Button, Col, Grid, Icon, List, Spinner } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import EmptyResults from 'components/EmptyResults'
import ShowMoreButton from 'components/ShowMoreButton'
import { useTableConfig } from 'modules/Services/Network/subcomponents/AccountsList/hooks/useTableConfig'
import FilterPanel from 'components/Filters/FilterPanel'
import { useGroupActionConfig } from 'modules/Services/Network/subcomponents/AccountsList/hooks/useGroupActionConfig'
import styles from 'modules/Services/Network/subcomponents/AccountsList/account-list.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import { Link, useParams } from 'react-router-dom'
import { notificationShow } from 'modules/Notifications/actions'
import { createPath } from 'history'
import qs from 'qs'
import {
  ACCOUNTS_ARCHIVE_TOGGLE,
  NETWORK_SET_SORTING,
  SET_SELECTION,
  UPDATE_ACCOUNT_NAME,
} from 'modules/Services/types'

const AccountList = ({
  className = '',
  display_balance = true,
  networkType,
  filterManager,
  networkId,
  setPage,
}) => {
  const { t } = useTranslation(['services'])
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const [getActionConfig, runGroupAction] = useGroupActionConfig()
  const { i18n } = useParams()

  const {
    accounts,
    has_account,
    selection: { ids: selectionIds = [], processing: selectionProcessing },
    texts = [],
  } = useSelector((state) => state['services/network'])

  const { pagination, data = [], sorting, loading, loaded } = accounts

  const canBeDeleted = useMemo(
    () => ['vk', 'vk-ads', 'targetmail'].includes(networkType),
    [networkType],
  )

  const hasAccessNewAction = useMemo(
    () => ['yd', 'yandex-business'].includes(networkType),
    [networkType],
  )

  const setSorting = useCallback(
    (value) => dispatch({ type: NETWORK_SET_SORTING, payload: value }),
    [],
  )

  const processGroupAction = useCallback(
    (groupActionType) => {
      const groupActionConfig = getActionConfig(groupActionType)
      runGroupAction({ groupActionConfig, ids: selectionIds })
    },
    [selectionIds, getActionConfig],
  )

  const updateSelection = (list) => {
    dispatch({
      type: SET_SELECTION,
      payload: list,
    })
  }

  const applyAccountName = useCallback(
    (id, newValue) =>
      new Promise((resolve, reject) =>
        dispatch({
          type: UPDATE_ACCOUNT_NAME,
          payload: { id, name: newValue },
          meta: { resolve, reject },
        }),
      ).then(() =>
        dispatch(
          notificationShow({
            type: 'success',
            message: 'common::notifications.data_success_stored',
            delay: 2000,
          }),
        ),
      ),
    [],
  )

  const accountsData = useMemo(() => {
    if (!hasAccessNewAction) {
      return [...data]
    }
    return data.map(({ extra_actions = [], ...rest }) => {
      const accessIndex = extra_actions.indexOf('access')
      if (accessIndex > -1) {
        extra_actions.splice(accessIndex, 1)
        extra_actions.push('access_new')
      }
      return { extra_actions, ...rest }
    })
  }, [data, hasAccessNewAction])

  const { tableState, tableConfig } = useTableConfig(
    {
      display_balance,
      updateSelection,
      selectionIds,
      i18n,
      networkType,
      filters: filterManager.filters,
      applyAccountName,
    },
    accountsData,
  )

  const manualHandleClick = useCallback(
    () =>
      dispatch({
        type: 'MODAL/SHOW',
        payload: {
          modalType: 'networkText',
          modalProps: {
            networkId,
            textKey: 'manual',
            shouldCloseOnOverlayClick: true,
          },
        },
      }),
    [networkId],
  )

  const canSelectedAccountsToppedUp = useMemo(
    () =>
      accounts?.data?.reduce((acc, { id, main_action }) => {
        if (selectionIds.includes(id) && main_action === 'deposit') {
          acc.push(id)
        }
        return acc
      }, []).length > 0,
    [selectionIds, accounts],
  )

  const toggleArchiveGroupAction = useCallback(
    (toArchive) => {
      dispatch({
        type: ACCOUNTS_ARCHIVE_TOGGLE,
        payload: {
          toArchive,
          ids: selectionIds,
        },
      })
    },
    [selectionIds],
  )

  return (
    <div className={className}>
      <FilterPanel
        filterManager={filterManager}
        className={'margin-bottom-32'}
      />
      {loaded && data.length !== 0 && (
        <DropdownSortingMenu
          className={styles['accounts-list__sort-menu']}
          buttonLabelPrefix={''}
          sortingRuleMap={['name', 'created_at', ...(display_balance ? ['balance'] : [])].reduce(
            (acc, key) => ({
              ...acc,
              [key]: { asc: `${key}.asc`, desc: `${key}.desc` },
            }),
            {},
          )}
          i18nKey={'services::account_list.filter_controls.sortmenu'}
          id={'accounts-list-sort-menu'}
          sorting={sorting}
          onSortChange={setSorting}
        />
      )}

      <div
        className={clsx(styles['accounts-list__group-actions-slider'], {
          [styles['accounts-list__group-actions-slider_active']]: !!selectionIds.length,
        })}
      >
        {!loading && (
          <Grid
            alignItems="center"
            justifyContent="between"
            className={styles['accounts-list__group-actions-wrapper']}
          >
            <Grid
              fluid={false}
              gap={16}
              className={styles['accounts-list__group-actions']}
            >
              {!filterManager?.filters?.is_archived && canSelectedAccountsToppedUp && (
                <Button
                  startIcon={
                    <Icon
                      inheritColor
                      name="card"
                    />
                  }
                  variant={'primary'}
                  size={'s'}
                  component={Link}
                  to={createPath({
                    pathname: pathGen(ROUTES.BILLING_TOP_UP_ACCOUNTS, {
                      networkType,
                    }),
                    search: qs.stringify({ ids: selectionIds || [] }, { arrayFormat: 'brackets' }),
                  })}
                >
                  {t('services::account_list.actions.deposit_accounts')}
                </Button>
              )}

              {filterManager?.filters?.is_archived ? (
                <Button
                  type="button"
                  variant={'secondary'}
                  grey
                  size="s"
                  disabled={selectionProcessing}
                  startIcon={
                    selectionProcessing ? (
                      <Spinner
                        inheritColor
                        width={20}
                      />
                    ) : (
                      <Icon
                        inheritColor
                        name="inbox-upload"
                      />
                    )
                  }
                  onClick={() => toggleArchiveGroupAction(false)}
                >
                  {t('services::account_list.extra_actions.unarchive')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={'secondary'}
                  grey
                  size="s"
                  disabled={selectionProcessing}
                  startIcon={
                    selectionProcessing ? (
                      <Spinner
                        inheritColor
                        width={20}
                      />
                    ) : (
                      <Icon
                        inheritColor
                        name="inbox-archive"
                      />
                    )
                  }
                  onClick={() => toggleArchiveGroupAction(true)}
                >
                  {t('services::account_list.extra_actions.archive')}
                </Button>
              )}

              {canBeDeleted && (
                <Button
                  variant={'ghost'}
                  className={styles['accounts-list__group-actions-button']}
                  grey
                  disabled={selectionProcessing}
                  size="s"
                  startIcon={
                    selectionProcessing ? (
                      <Spinner
                        inheritColor
                        width={20}
                      />
                    ) : (
                      <Icon
                        inheritColor
                        name="delete"
                      />
                    )
                  }
                  onClick={() => processGroupAction('group_delete')}
                >
                  {t('services::account_list.extra_actions.delete')}
                </Button>
              )}
            </Grid>
            <Col className={styles['accounts-list__group-actions-selected-accounts']}>
              {t('services::account_list.checkedAccounts', {
                count: selectionIds.length,
              })}
            </Col>
          </Grid>
        )}
      </div>

      {data.length === 0 && (!networkType || loading) && (
        <div className={styles['accounts-list__spinner-container']}>
          <GridSpinner
            width={32}
            style={{ height: '100%' }}
          />
        </div>
      )}

      {loaded && !loading && data.length === 0 ? (
        <EmptyResults
          header={t('services::account_list.data_list.empty.heading')}
          text={
            has_account ? (
              t('services::account_list.data_list.empty.text')
            ) : texts.find(({ key }) => key === 'manual') ? (
              <Trans
                i18nKey="services::account_list.rules_for_connecting"
                link={t('services::account_list.rules_for_connecting_link')}
              >
                Ознакомьтесь c
                <Button
                  color={'Label-GU-Primary'}
                  size={'l'}
                  variant={'ghost'}
                  link
                  type={'button'}
                  onClick={manualHandleClick}
                >
                  правилами подключения
                </Button>
                аккаунтов
              </Trans>
            ) : null
          }
          icon={<Icon name={'users'} />}
        />
      ) : loaded && data.length !== 0 ? (
        <>
          <List
            Loader={() => (
              <GridSpinner
                width={32}
                style={{ height: '100%' }}
              />
            )}
            table={tableState}
            config={tableConfig}
            EmptyComponent={
              !loading && data.length === 0
                ? () => (
                    <EmptyResults
                      header={t('services::account_list.empty_list_heading')}
                      text={
                        has_account ? (
                          t('services::account_list.empty_list_label')
                        ) : texts.find(({ key }) => key === 'manual') ? (
                          <Trans
                            i18nKey="services::account_list.rules_for_connecting"
                            link={t('services::account_list.rules_for_connecting_link')}
                          >
                            Ознакомьтесь c
                            <Button
                              color={'Label-GU-Primary'}
                              size={'l'}
                              variant={'ghost'}
                              link
                              type={'button'}
                              onClick={manualHandleClick}
                            >
                              правилами подключения
                            </Button>
                            аккаунтов
                          </Trans>
                        ) : null
                      }
                      className={styles['accounts-list__empty-results']}
                      icon={<Icon name={'users'} />}
                    />
                  )
                : null
            }
          />
          <ShowMoreButton
            onClick={() => setPage((prev) => prev + 1)}
            loading={loading}
            current={data.length}
            total={pagination.total}
          />
        </>
      ) : null}
    </div>
  )
}

AccountList.propTypes = {
  className: PropTypes.string,
  networkType: PropTypes.string,
  filterManager: PropTypes.object,
  networkId: PropTypes.string,
  display_balance: PropTypes.bool,
  accounts: PropTypes.object,
  setPage: PropTypes.func,
}

export default AccountList
